import { merge, uniq } from 'lodash-es';
import type { NormalizedSchema } from 'normalizr';
import { normalize } from 'normalizr';
import type {
  ActivityEntities,
  ActivityQuery,
  ActivityRaw,
  ActivityResult,
  State,
} from 'redux/modules/activities.types';
import {
  clearActivitiesCreator,
  fetchedActivities,
  withXhrStatusReducer,
} from 'redux/modules/activities.types';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import {
  ENTITY_CONTEXT_SCHEMA_KEY,
  PROJECT_ACTIVITY_SCHEMA_KEY,
  projectActivityListSchema,
  USER_ACTIVITY_SCHEMA_KEY,
  USERS_SCHEMA_KEY,
  userActivityListSchema,
} from 'schema/activities';
import { reducerWithInitialState } from 'typescript-fsa-reducers';

export const initialEntities: ActivityEntities = {
  // Actual activities
  [PROJECT_ACTIVITY_SCHEMA_KEY]: {},
  [USER_ACTIVITY_SCHEMA_KEY]: {},
  // Associations to activities
  [ENTITY_CONTEXT_SCHEMA_KEY]: {},
  [USERS_SCHEMA_KEY]: {},
};

export const initialResult: ActivityResult = {
  [PROJECT_ACTIVITY_SCHEMA_KEY]: [],
  [USER_ACTIVITY_SCHEMA_KEY]: [],
};

export const initialState: State = {
  entities: initialEntities,
  result: initialResult,
  xhrStatus: xhrStatusFactory(),
  maybeHasMore: false,
};

function mergeStateWithNormalizedData(
  state: State,
  query: ActivityQuery,
  normalizedData: NormalizedSchema<ActivityEntities, Array<number>>,
  key: 'projectActivities' | 'userActivities'
): State {
  const { entities, result } = normalizedData;

  return {
    ...state,
    entities: merge({}, state.entities, entities),
    result: {
      ...state.result,
      [key]: uniq([...state.result[key], ...result]),
    },
    maybeHasMore: query.limit === result.length,
  };
}

const reducer = reducerWithInitialState<State>(initialState)
  .case(clearActivitiesCreator, () => initialState)
  .caseWithAction(fetchedActivities, (state, action) => {
    switch (action.payload.params.filter) {
      case 'projects': {
        const normalized = normalize<ActivityRaw, ActivityEntities, Array<number>>(
          action.payload.result,
          projectActivityListSchema
        );
        return mergeStateWithNormalizedData(
          state,
          action.payload.params.query,
          normalized,
          PROJECT_ACTIVITY_SCHEMA_KEY
        );
      }
      case 'users': {
        const normalized = normalize<ActivityRaw, ActivityEntities, Array<number>>(
          action.payload.result,
          userActivityListSchema
        );

        return mergeStateWithNormalizedData(
          state,
          action.payload.params.query,
          normalized,
          USER_ACTIVITY_SCHEMA_KEY
        );
      }
      default:
        return state;
    }
  });

export default withXhrStatusReducer(reducer);
